import { useState, useEffect, useRef } from 'react';
import { ProxyImage } from '../../lib/imageProxy';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Upload,
  X,
  Tag,
  Layers,
  Grid,
  Star,
  Percent,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Package,
  GripVertical,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Category {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number;
  main_image: string;
  stock: number;
  category?: Category;
}

interface Collection {
  id: string;
  name: string;
  description: string;
  type: 'category_mix' | 'new' | 'featured' | 'sale';
  image: string | null;
  order: number;
  is_active: boolean;
  category_ids: string[] | null;
  products?: Product[];
  created_at: string;
}

export default function CollectionsTab() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [collectionType, setCollectionType] = useState<Collection['type']>('category_mix');
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCollections();
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('product_collections')
        .select(`
          id,
          name,
          description,
          type,
          image,
          order,
          is_active,
          category_ids,
          created_at,
          collection_products (
            product_id,
            products (
              id,
              name,
              description,
              base_price,
              main_image,
              stock,
              category_id
            )
          )
        `)
        .order('order', { ascending: true });

      if (error) throw error;

      const transformedCollections: Collection[] = [];
      if (data) {
        for (const collection of data) {
          const transformedProducts: Product[] = [];
          if (collection.collection_products) {
            for (const item of collection.collection_products) {
              if (item.products) {
                const p: any = item.products;
                let cat: Category | undefined = undefined;
                if (p.category_id && categories.length > 0) {
                  const foundCat = categories.find(c => c.id === p.category_id);
                  if (foundCat) {
                    cat = { id: foundCat.id, name: foundCat.name };
                  }
                }
                transformedProducts.push({
                  id: p.id,
                  name: p.name,
                  description: p.description,
                  base_price: p.base_price,
                  main_image: p.main_image,
                  stock: p.stock,
                  category: cat
                });
              }
            }
          }
          transformedCollections.push({
            id: collection.id,
            name: collection.name,
            description: collection.description,
            type: collection.type as Collection['type'],
            image: collection.image,
            order: collection.order,
            is_active: collection.is_active,
            category_ids: collection.category_ids,
            products: transformedProducts,
            created_at: collection.created_at
          });
        }
      }
      setCollections(transformedCollections);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar colecciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .is('parent_id', null)
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, name')
        .is('parent_id', null);

      const categoryMap: Record<string, Category> = {};
      categoriesData?.forEach(cat => {
        categoryMap[cat.id] = { id: cat.id, name: cat.name };
      });

      const { data, error } = await supabase
        .from('products')
        .select('id, name, description, base_price, main_image, stock, category_id')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedProducts: Product[] = [];
      if (data) {
        for (const item of data) {
          transformedProducts.push({
            id: item.id,
            name: item.name,
            description: item.description,
            base_price: item.base_price,
            main_image: item.main_image,
            stock: item.stock,
            category: item.category_id ? categoryMap[item.category_id] : undefined
          });
        }
      }
      setProducts(transformedProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImage(true);
      setError(null);

      if (!file.type.startsWith('image/')) {
        throw new Error('El archivo debe ser una imagen');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('La imagen no debe superar los 5MB');
      }

      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `collections/${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(fileName);

      setPreviewImage(publicUrl);
      return publicUrl;
    } catch (err) {
      console.error('Error al subir la imagen:', err);
      setError(err instanceof Error ? err.message : 'Error al subir la imagen');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);

      const collectionData = {
        name: formData.get('name') as string,
        description: formData.get('description') as string,
        type: collectionType,
        image: previewImage || editingCollection?.image,
        order: parseInt(formData.get('order') as string) || 0,
        is_active: isActive,
        category_ids: collectionType === 'category_mix' ? selectedCategories : null
      };

      let collectionId: string;

      if (editingCollection) {
        const { error } = await supabase
          .from('product_collections')
          .update(collectionData)
          .eq('id', editingCollection.id);

        if (error) throw error;
        collectionId = editingCollection.id;

        await supabase
          .from('collection_products')
          .delete()
          .eq('collection_id', editingCollection.id);
      } else {
        const { data, error } = await supabase
          .from('product_collections')
          .insert([collectionData])
          .select()
          .single();

        if (error) throw error;
        collectionId = data.id;
      }

      if (selectedProducts.length > 0) {
        const productsToInsert = selectedProducts.map((productId, index) => ({
          collection_id: collectionId,
          product_id: productId,
          order: index
        }));

        const { error: productsError } = await supabase
          .from('collection_products')
          .insert(productsToInsert);

        if (productsError) throw productsError;
      }

      await fetchCollections();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving collection:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar la colección');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCollection = (collection: Collection) => {
    setEditingCollection(collection);
    setCollectionType(collection.type);
    setPreviewImage(collection.image);
    setSelectedCategories(collection.category_ids || []);
    setSelectedProducts(collection.products?.map(p => p.id) || []);
    setIsActive(collection.is_active);
    setShowAddModal(true);
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!confirm('¿Eliminar esta colección?')) return;

    try {
      const { error } = await supabase
        .from('product_collections')
        .delete()
        .eq('id', collectionId);

      if (error) throw error;
      await fetchCollections();
    } catch (err) {
      console.error('Error deleting collection:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const toggleCollectionActive = async (collection: Collection) => {
    try {
      const { error } = await supabase
        .from('product_collections')
        .update({ is_active: !collection.is_active })
        .eq('id', collection.id);

      if (error) throw error;

      setCollections(prev =>
        prev.map(c =>
          c.id === collection.id ? { ...c, is_active: !c.is_active } : c
        )
      );
    } catch (err) {
      console.error('Error toggling collection:', err);
    }
  };

  const updateCollectionOrder = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = collections.findIndex(c => c.id === id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= collections.length) return;

    const currentCollection = collections[currentIndex];
    const targetCollection = collections[targetIndex];

    try {
      await supabase
        .from('product_collections')
        .update({ order: targetCollection.order })
        .eq('id', currentCollection.id);

      await supabase
        .from('product_collections')
        .update({ order: currentCollection.order })
        .eq('id', targetCollection.id);

      await fetchCollections();
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  const resetForm = () => {
    setEditingCollection(null);
    setPreviewImage(null);
    setSelectedProducts([]);
    setSelectedCategories([]);
    setCollectionType('category_mix');
    setProductSearchTerm('');
    setIsActive(true);
    setError(null);
  };

  const getCollectionTypeIcon = (type: Collection['type']) => {
    switch (type) {
      case 'category_mix': return <Grid className="w-4 h-4" />;
      case 'new': return <Tag className="w-4 h-4" />;
      case 'featured': return <Star className="w-4 h-4" />;
      case 'sale': return <Percent className="w-4 h-4" />;
    }
  };

  const getCollectionTypeLabel = (type: Collection['type']) => {
    switch (type) {
      case 'category_mix': return 'Categorías';
      case 'new': return 'Nuevos';
      case 'featured': return 'Destacados';
      case 'sale': return 'Ofertas';
    }
  };

  const getCollectionTypeColor = (type: Collection['type']) => {
    switch (type) {
      case 'category_mix': return 'bg-purple-100 text-purple-700';
      case 'new': return 'bg-green-100 text-green-700';
      case 'featured': return 'bg-amber-100 text-amber-700';
      case 'sale': return 'bg-red-100 text-red-700';
    }
  };

  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = collections.filter(c => c.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Colecciones</h2>
          <p className="text-sm text-gray-500 mt-1">
            {collections.length} colecciones ({activeCount} activas)
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva colección
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{collections.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
              <p className="text-xs text-gray-500">Activas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
              <EyeOff className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{collections.length - activeCount}</p>
              <p className="text-xs text-gray-500">Inactivas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {collections.reduce((sum, c) => sum + (c.products?.length || 0), 0)}
              </p>
              <p className="text-xs text-gray-500">Productos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar colecciones..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Collections List */}
      <div className="bg-white rounded-xl border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredCollections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Layers className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Sin colecciones</h3>
            <p className="text-sm text-gray-500 mb-4">Crea tu primera colección de productos</p>
            <button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nueva colección
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredCollections.map((collection, index) => (
              <div
                key={collection.id}
                className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
                  !collection.is_active ? 'opacity-60' : ''
                }`}
              >
                {/* Order Controls */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => updateCollectionOrder(collection.id, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <span className="text-xs text-gray-400 text-center">{index + 1}</span>
                  <button
                    onClick={() => updateCollectionOrder(collection.id, 'down')}
                    disabled={index === filteredCollections.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Image */}
                <div className="w-24 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {collection.image ? (
                    <ProxyImage
                      src={collection.image}
                      alt={collection.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Layers className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900 truncate">{collection.name}</h4>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${getCollectionTypeColor(collection.type)}`}>
                      {getCollectionTypeIcon(collection.type)}
                      {getCollectionTypeLabel(collection.type)}
                    </span>
                    {!collection.is_active && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        Inactiva
                      </span>
                    )}
                  </div>
                  {collection.description && (
                    <p className="text-sm text-gray-500 truncate">{collection.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {collection.products?.length || 0} productos
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleCollectionActive(collection)}
                    className={`p-2 rounded-lg transition-colors ${
                      collection.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={collection.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {collection.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleEditCollection(collection)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCollection(collection.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingCollection ? 'Editar colección' : 'Nueva colección'}
              </h3>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={editingCollection?.name}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nombre de la colección"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo
                  </label>
                  <select
                    value={collectionType}
                    onChange={(e) => setCollectionType(e.target.value as Collection['type'])}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="category_mix">Mezcla de Categorías</option>
                    <option value="new">Nuevos Productos</option>
                    <option value="featured">Destacados</option>
                    <option value="sale">Ofertas</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  name="description"
                  defaultValue={editingCollection?.description}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Descripción de la colección..."
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orden
                  </label>
                  <input
                    type="number"
                    name="order"
                    min="0"
                    defaultValue={editingCollection?.order || 0}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagen
                  </label>
                  <div className="flex items-center gap-3">
                    {(previewImage || editingCollection?.image) ? (
                      <div className="relative">
                        <ProxyImage
                          src={previewImage || editingCollection?.image || ''}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setPreviewImage(null)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Subir
                      </button>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Estado activo</p>
                  <p className="text-sm text-gray-500">La colección se mostrará en el sitio</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsActive(!isActive)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isActive ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      isActive ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Categories (for category_mix) */}
              {collectionType === 'category_mix' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categorías
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => {
                      const productsInCategory = products.filter(p => p.category?.id === category.id);
                      const isSelected = selectedCategories.includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedCategories(prev => prev.filter(id => id !== category.id));
                              const idsToRemove = productsInCategory.map(p => p.id);
                              setSelectedProducts(prev => prev.filter(id => !idsToRemove.includes(id)));
                            } else {
                              setSelectedCategories(prev => [...prev, category.id]);
                              const idsToAdd = productsInCategory.map(p => p.id);
                              setSelectedProducts(prev => [...new Set([...prev, ...idsToAdd])]);
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-sm">{category.name}</span>
                          <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                            {productsInCategory.length}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Products */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Productos ({selectedProducts.length} seleccionados)
                </label>
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg mb-3"
                />
                <div className="grid grid-cols-4 gap-3 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                  {products
                    .filter(p => p.name.toLowerCase().includes(productSearchTerm.toLowerCase()))
                    .map(product => {
                      const isSelected = selectedProducts.includes(product.id);
                      return (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedProducts(prev => prev.filter(id => id !== product.id));
                            } else {
                              setSelectedProducts(prev => [...prev, product.id]);
                            }
                          }}
                          className={`relative rounded-lg overflow-hidden border-2 transition-colors ${
                            isSelected ? 'border-blue-500' : 'border-transparent hover:border-gray-300'
                          }`}
                        >
                          <div className="aspect-square">
                            <ProxyImage
                              src={product.main_image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-1.5 bg-white">
                            <p className="text-xs truncate">{product.name}</p>
                          </div>
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Selected Products Summary */}
              {selectedProducts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Productos seleccionados
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {selectedProducts.map(pid => {
                      const prod = products.find(p => p.id === pid);
                      if (!prod) return null;
                      return (
                        <div
                          key={pid}
                          className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg"
                        >
                          <ProxyImage
                            src={prod.main_image}
                            alt={prod.name}
                            className="w-5 h-5 rounded object-cover"
                          />
                          <span className="text-xs text-blue-700 max-w-[100px] truncate">
                            {prod.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedProducts(prev => prev.filter(id => id !== pid))}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingCollection ? 'Guardar cambios' : 'Crear colección'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Tipos de colecciones</p>
          <ul className="list-disc list-inside space-y-1 text-blue-600">
            <li><strong>Mezcla de Categorías:</strong> Agrupa productos de múltiples categorías</li>
            <li><strong>Nuevos:</strong> Productos recién agregados</li>
            <li><strong>Destacados:</strong> Productos promocionados</li>
            <li><strong>Ofertas:</strong> Productos con descuento</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
